import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import heroImage from "@/assets/hero-surreal-lightbulb.jpg";
import heroImage960 from "@/assets/hero-surreal-lightbulb-960.jpg";
import heroImage480 from "@/assets/hero-surreal-lightbulb-480.jpg";

// Preload LCP image with responsive srcset so browser picks the right size
if (typeof document !== 'undefined' && !document.querySelector('link[data-hero-preload]')) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.setAttribute('imagesrcset', `${heroImage480} 480w, ${heroImage960} 960w, ${heroImage} 1920w`);
  link.setAttribute('imagesizes', '100vw');
  link.setAttribute('data-hero-preload', 'true');
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}
export function Hero() {
  const { t } = useTranslation('hero');
  
  return <section className="relative h-40 md:min-h-[100dvh] flex items-center justify-center overflow-hidden w-full max-w-full">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          srcSet={`${heroImage480} 480w, ${heroImage960} 960w, ${heroImage} 1920w`}
          sizes="100vw"
          alt={t('imageAlt')}
          fetchPriority="high"
          width={1920}
          height={1080}
          className="w-full h-full object-cover opacity-50 parallax-sketch"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/60" />
      </div>

      {/* Floating sketch elements - hidden on mobile */}
      <div className="hidden md:block absolute top-20 left-4 lg:left-10 animate-float-sketch opacity-20">
        <svg width="100" height="100" className="text-gray-medium">
          <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" />
          <line x1="20" y1="20" x2="80" y2="80" stroke="currentColor" strokeWidth="1" />
        </svg>
      </div>

      <div className="hidden md:block absolute bottom-32 right-4 lg:right-10 animate-float-sketch opacity-20" style={{
      animationDelay: '1.5s'
    }}>
        <svg width="80" height="80" className="text-gray-medium">
          <rect x="20" y="20" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="40" cy="40" r="15" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="3,3" />
        </svg>
      </div>


      {/* Studio Gool 7 - centralized outside box with larger emphasis */}
      <div className="absolute top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 px-4">
        <h2 className="font-surgena text-3xl md:text-7xl lg:text-8xl text-gray-dark font-black tracking-tight filter brightness-110 drop-shadow-xl text-center">
          {t('title')}
        </h2>
      </div>

      {/* Description box with 30% opacity - hidden on mobile */}
      <div className="hidden md:block absolute bottom-[15%] sm:bottom-[20%] left-1/2 transform -translate-x-1/2 z-10 w-full max-w-2xl px-4">
        <div className="bg-background/30 border border-gray-dark p-4 sm:p-6 md:p-8 text-center mx-2">
          <p className="text-sm sm:text-sketch-md md:text-lg text-gray-medium leading-relaxed">
            {t('subtitle')}
            <br className="hidden sm:block" />
            <span className="text-xs sm:text-sm font-medium text-gray-medium/80 block sm:inline mt-1 sm:mt-0">{t('description')}</span>
          </p>
        </div>
      </div>

      {/* Floating annotations - hidden on mobile */}
      <div className="hidden lg:block absolute top-16 left-8 text-xs text-gray-medium font-mono rotate-12 opacity-70 z-10">
        {t('prototypeLabel')}
      </div>
      <div className="hidden lg:block absolute bottom-32 right-8 text-xs text-gray-medium font-mono -rotate-6 opacity-70 z-10">
        {t('labNotesLabel')}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-gray-medium rounded-full flex justify-center">
          <div className="w-1 h-3 bg-gray-medium rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>;
}